import React, { useMemo, useContext } from 'react';

import { QuestionError } from '@sm/question-definitions';
import {
  SingleQuestionController,
  ExternalQuestion,
  SingleQuestionControllerProps,
  RespondentDisplayOptions,
} from '@sm/question-widgets/respondent-survey';
import { SurveyFormatView } from '~app/components/Survey/SurveyFormat';
import groupQuestionsWithLayoutConfig from './utils';
import { QuestionRowLayout } from '../QuestionRowLayout';
import { QuestionAnswers } from '../../types';
import { CurrentPageContext } from '../../CurrentPageContext';
import { useSurveyErrorsContext } from '../../errors';
import transformFromSurveyErrors from '../../errors/transformFromSurveyErrors';

export type Props = {
  questions: ExternalQuestion[];
  answers: QuestionAnswers;
  /** Checks if the question coming in is OQAAT is default set to true */
  isOQAAT?: boolean;
};

type SingleQuestionControllerWrapperProps = Pick<SingleQuestionControllerProps, 'question'> & RespondentDisplayOptions;

/** Wrapper to flatten `displayOptions` */
function SingleQuestionControllerWrapper({
  question,
  ...displayOptions
}: SingleQuestionControllerWrapperProps): React.ReactElement {
  return <SingleQuestionController question={question} displayOptions={displayOptions} />;
}

function QuestionList(this: void, { questions, answers, isOQAAT = true }: Props): React.ReactElement {
  const { registerValidator, unregisterValidator } = useContext(CurrentPageContext);
  const { getErrorsById } = useSurveyErrorsContext();
  const groups = useMemo(() => groupQuestionsWithLayoutConfig(questions), [questions]);

  return (
    <>
      {groups.map(group => (
        <QuestionRowLayout
          isOQAAT={isOQAAT}
          maxQuestionsPerRow={isOQAAT ? 1 : 4}
          key={`group_${group.map(g => g.id).concat('-')}`}
        >
          {group.map(question => (
            <SurveyFormatView name={question.id} key={question.id}>
              {/** `active` and `onSubmit` get automatically applied by `SurveyFormatView` */}
              <SingleQuestionControllerWrapper
                question={question}
                answer={answers[question.id]}
                errors={transformFromSurveyErrors<QuestionError>(getErrorsById(question.id))}
                registerValidator={registerValidator}
                unregisterValidator={unregisterValidator}
              />
            </SurveyFormatView>
          ))}
        </QuestionRowLayout>
      ))}
    </>
  );
}

export default QuestionList;
